import React, { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import { AuthDialog } from '../components/AuthDialog/AuthDialog';
import { useForm } from 'react-hook-form';
import { TextInputController } from '../components/InputControllers/TextInputController';
import { PasswordInputController } from '../components/InputControllers/PasswordInputController';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { HiddenSubmit } from '../components/HiddenSubmit/HiddenSubmit';
import { useHistory, useParams } from 'react-router-dom';
import { createFetcher } from '../contexts/Fetcher';
import { apiOrganization } from '../resources/api/apiOrganization';
import { AuthContext } from '../contexts/AuthContext';
import { useApiErrorHandler } from '../hooks/useApiErrorHandler';
import { AuthenticatedRoutes } from '../routers/AuthenticatedRoutes';
import { NotificationContext } from '../contexts/NotificationContext';
import Alert from '@mui/material/Alert';
import { GuestRoutes } from '../routers/GuestRouter';
import { CurrentUserContext } from '../contexts/CurrentUserContext';
import { useTranslation } from 'react-i18next';
import Box from '@mui/system/Box';
import { Agreements } from '../components/Agreements/Agreements';
import { EInvitationStatus } from '../resources/models/IInvitationDetails';
import { AuthGoBack } from '../components/AuthDialog/AuthGoBack';
import { parseJwtPayload } from '../helpers/parseJwtPayload';

interface IInvitationTokenPayload {
  first_name?: string;
  last_name?: string;
  invited_by?: string;
}

interface IFormFields {
  firstName: string;
  lastName: string;
  password: string;
}

interface IParams {
  token: string;
}

const { useGetInvitationDetails, useAcceptInvitation, useAcceptInvitationAndSignUp } = apiOrganization;

const InvitationDetailsFetcher = createFetcher(() => {
  const { token } = useParams<IParams>();
  return useGetInvitationDetails(token).execute;
});

const JoinComponent: FC = () => {
  const { token } = useParams<IParams>();

  const tokenPayload = useMemo(() => {
    try {
      return parseJwtPayload<IInvitationTokenPayload>(token);
    } catch (e) {
      return null;
    }
  }, [token]);

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<IFormFields>({
    defaultValues: {
      firstName: tokenPayload?.first_name ?? '',
      lastName: tokenPayload?.last_name ?? '',
    },
  });
  const { t } = useTranslation();

  const { data } = useContext(InvitationDetailsFetcher.Context);
  const { identity, identities, setIdentity } = useContext(AuthContext);
  const { refetchUserInfo } = useContext(CurrentUserContext);

  const handleError = useApiErrorHandler();
  const history = useHistory();
  const { addNotification } = useContext(NotificationContext);

  const { execute: acceptInvitation } = useAcceptInvitation(data?.payload.organization_name, token);
  const { execute: signUpAndAccept } = useAcceptInvitationAndSignUp(data?.payload.organization_name, token);

  const invitedBy = tokenPayload?.invited_by;

  const onSignUpAndJoin = useCallback(
    async (formData: IFormFields) => {
      if (!data) return;

      try {
        await signUpAndAccept({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: data.payload.email,
          password: formData.password,
        });

        const query = new URLSearchParams({
          email: data.payload.email,
        });

        history.push(`${GuestRoutes.SignIn()}?${query}`);
      } catch (e) {
        handleError(e);
      }
    },
    [data]
  );

  const onAccept = useCallback(async () => {
    try {
      await acceptInvitation();
      addNotification({
        severity: 'success',
        message: t('invitation.success', { organization: data?.payload.organization_name }),
      });
      await refetchUserInfo();
      history.push(AuthenticatedRoutes.ChooseOrganization());
    } catch (e) {
      handleError(e);
    }
  }, [acceptInvitation, refetchUserInfo, data?.payload.organization_name]);

  const buttons = useMemo(() => {
    const matchingIdentity = identities.find(({ email }) => email === data?.payload.email);

    if (data?.payload.force_signup) {
      return [
        <Button key="submit" variant="contained" onClick={handleSubmit(onSignUpAndJoin)}>
          {t('invitation.button.signUp')}
        </Button>,
      ];
    } else if (data?.payload.email === identity?.email) {
      return [
        <Button key="submit" variant="contained" onClick={onAccept}>
          {t('invitation.button.join', { organization: data?.payload.organization_name })}
        </Button>,
      ];
    } else if (matchingIdentity) {
      /** @todo handle situation when token timed out and need to enter credentials again */
      const onSwitch = () => {
        setIdentity(matchingIdentity);
      };
      return [
        <Button key="submit" variant="contained" onClick={onSwitch}>
          {t('invitation.button.switch')}
        </Button>,
      ];
    } else {
      /** @todo redirection must happen now */
      return [];
    }
  }, [handleSubmit, onSignUpAndJoin, onAccept, identity?.email, data, identities]);

  useEffect(() => {
    if (!data || data.payload.force_signup) return;
    const { email } = data.payload;

    if (!identities.some((identity) => identity.email === email)) {
      const query = new URLSearchParams({
        redirect: location.pathname,
        email,
        flow: 'invitation',
      });
      history.push(`${GuestRoutes.SignIn()}?${query}`);
    }
  }, [data, identities]);

  if (!data) return null;

  if (data.payload.status !== EInvitationStatus.Pending) {
    return (
      <AuthDialog title={t('invitation.invalid.title')} actions={[<AuthGoBack key="back" to={GuestRoutes.SignIn()} />]}>
        <Alert severity="error">{t('invitation.invalid.description')}</Alert>
      </AuthDialog>
    );
  }

  return (
    <AuthDialog
      title={t('invitation.header', { organization: data.payload.organization_name })}
      subtitle={t('invitation.subheader', { name: invitedBy })}
      actions={buttons}
    >
      {identity && identity.email !== data?.payload.email ? (
        <Box mb={2}>
          <Alert severity="info">
            {t(!data.payload.force_signup ? 'invitation.alert.switchAccount' : 'invitation.alert.signUp', {
              email: identity.email,
            })}
          </Alert>
        </Box>
      ) : null}

      {data.payload.force_signup ? (
        <Grid container rowSpacing={0} columnSpacing={2} component={'form'} onSubmit={handleSubmit(onSignUpAndJoin)}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('common.formLabel.email')}
              type={'text'}
              disabled={true}
              value={data.payload.email}
              InputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextInputController
              control={control}
              errors={errors}
              label={t('common.formLabel.firstName')}
              name={'firstName'}
              rules={{ required: true }}
              InputProps={{
                autoComplete: 'given-name',
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextInputController
              control={control}
              errors={errors}
              label={t('common.formLabel.lastName')}
              name={'lastName'}
              rules={{ required: true }}
              InputProps={{
                autoComplete: 'family-name',
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <PasswordInputController
              control={control}
              errors={errors}
              label={t('invitation.setPassword')}
              name={'password'}
              rules={{ required: true }}
              InputProps={{
                autoComplete: 'new-password',
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Agreements />
          </Grid>

          <HiddenSubmit />
        </Grid>
      ) : null}
    </AuthDialog>
  );
};

export const JoinPage: FC = () => {
  return (
    <InvitationDetailsFetcher.WAF>
      <JoinComponent />
    </InvitationDetailsFetcher.WAF>
  );
};
